/**
 * SVGCanvas - Main rendering component
 *
 * Renders molecular structures using contexts for composition.
 * This component reads from all available contexts and renders accordingly.
 */

import React, { forwardRef, useCallback, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useMolecularSVG } from '../contexts/MolecularSVGContext';
import { useSVGGeneration } from '../rendering/useSVGGeneration';
import { Defs } from './Defs';
import { renderBondFromRecipe } from '../rendering/renderBondFromRecipe';
import { useFormatOverride } from '../rendering/contexts/FormatOverrideContext';
import { NavigableTree, type ElementType } from './NavigableTree';
import { buildStructure } from '../structure/buildStructure';
import type { TreeLabelMap } from '../structure/buildAltTextMap';
import type { WorkspaceFeatureResult } from '@alchemiesolns/feature-extractor-compat';

export interface SVGCanvasProps {
  /**
   * Additional CSS class name for the SVG element
   */
  className?: string;

  /**
   * Additional inline styles for the SVG element
   */
  style?: CSSProperties;

  /**
   * Whether to show the navigable ARIA tree for screen reader accessibility
   */
  showNavigableTree?: boolean;

  /**
   * Tree label map for navigable tree elements.
   * If not provided, labels from buildStructure will be used.
   */
  treeLabelMap?: TreeLabelMap;

  /**
   * Feature extraction result for enhanced structure building
   * If provided, atoms/bonds will be grouped by chemical features
   */
  featureResult?: WorkspaceFeatureResult;

  /**
   * Callback when an element is selected via the navigable tree
   */
  onTreeSelect?: (elementId: string, elementType: ElementType) => void;

  /**
   * Callback when focus changes in the navigable tree
   */
  onTreeFocusChange?: (elementId: string | null, elementType: ElementType | null) => void;

  /**
   * Whether to show the navigable tree for debugging (makes it slightly visible)
   */
  debugNavigableTree?: boolean;

  /**
   * Override the auto-calculated viewBox with a custom value.
   * When set, this viewBox is used instead of the one from useSVGGeneration.
   * The background rect is recalculated to match the overridden viewBox.
   */
  viewBoxOverride?: string;

  /**
   * Start the navigable tree with all nodes expanded.
   * Useful for SVG export/download where the full structure should be accessible.
   */
  defaultExpandAll?: boolean;

  /**
   * Children rendered at the end of the SVG (e.g., HTMLStructureTree for accessibility export).
   * The parent component is responsible for building the structure and passing the tree component.
   */
  children?: React.ReactNode;
}

/**
 * SVGCanvas Component
 *
 * Main rendering component that composes all features based on active contexts:
 * - MolecularSVGContext: Required - provides workspace data
 * - FormatContext: Optional - visual styling
 * - MotionContext: Optional - Framer Motion integration
 * - ZoomPanContext: Optional - zoom/pan navigation
 * - SelectionContext: Optional - element selection
 * - DragContext: Optional - atom dragging
 * - ArrowDrawingContext: Optional - arrow drawing
 *
 * Usage (minimal):
 * ```tsx
 * <MolecularSVGProvider workspace={workspace}>
 *   <SVGCanvas />
 * </MolecularSVGProvider>
 * ```
 *
 * Usage (with features):
 * ```tsx
 * <MolecularSVGProvider workspace={workspace}>
 *   <FormatProvider>
 *     <ZoomPanProvider>
 *       <SVGCanvas />
 *     </ZoomPanProvider>
 *   </FormatProvider>
 * </MolecularSVGProvider>
 * ```
 */
export const SVGCanvas = forwardRef<SVGSVGElement, SVGCanvasProps>(
  function SVGCanvas({
    className,
    style, viewBoxOverride,
    showNavigableTree = false,
    treeLabelMap,
    featureResult,
    onTreeSelect,
    onTreeFocusChange,
    debugNavigableTree = false,
    defaultExpandAll = false,
    children,
  }, ref) {
    // Layer 1: Data - Get workspace from context
    const { workspace, svgWidth, svgHeight, padding, alignment } = useMolecularSVG();

    // Layer 2: Visual - Generate SVG elements using existing useSVGGeneration
    const { atoms, bondRecipes, electrons, charges, arrows, calculatedViewBox, backgroundRect: autoBackgroundRect } = useSVGGeneration(
      workspace,
      svgWidth,
      svgHeight,
      alignment
    );

    // Measure true graphical bounding box from rendered SVG content.
    // Only measure once per calculatedViewBox change (i.e. when the molecule changes).
    const localRef = useRef<SVGSVGElement>(null);
    const [measuredViewBox, setMeasuredViewBox] = useState<string | null>(null);
    const lastMeasuredForRef = useRef<string | null>(null);

    // Merge forwarded ref with local ref
    const mergedRef = useCallback(
      (node: SVGSVGElement | null) => {
        localRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    // Reset measurement when the underlying config changes
    if (lastMeasuredForRef.current !== null && lastMeasuredForRef.current !== calculatedViewBox) {
      lastMeasuredForRef.current = null;
      setMeasuredViewBox(null);
    }

    useLayoutEffect(() => {
      const svg = localRef.current;
      // Skip if: explicit override, already measured for this viewBox, or no element
      if (!svg || viewBoxOverride || lastMeasuredForRef.current === calculatedViewBox) return;

      try {
        const bbox = svg.getBBox();
        if (bbox.width > 0 && bbox.height > 0) {
          const p = padding;

          // Fit rendered bounds to SVG aspect ratio, then add uniform padding
          const svgAspect = svgWidth / svgHeight;
          const contentAspect = bbox.width / bbox.height;

          let fitW, fitH;
          if (contentAspect > svgAspect) {
            fitW = bbox.width;
            fitH = bbox.width / svgAspect;
          } else {
            fitH = bbox.height;
            fitW = bbox.height * svgAspect;
          }

          const vbW = fitW + 2 * p;
          const vbH = fitH + 2 * p;
          const vbX = (bbox.x + bbox.width / 2) - vbW / 2;
          const vbY = (bbox.y + bbox.height / 2) - vbH / 2;

          lastMeasuredForRef.current = calculatedViewBox;
          setMeasuredViewBox(`${vbX} ${vbY} ${vbW} ${vbH}`);
        }
      } catch {
        // getBBox() can throw if SVG is not in the DOM yet
      }
    }, [calculatedViewBox, viewBoxOverride, padding, svgWidth, svgHeight]);

    // Priority: viewBoxOverride > measuredViewBox > calculatedViewBox
    const viewBox = viewBoxOverride ?? measuredViewBox ?? calculatedViewBox;
    const backgroundRect = (viewBoxOverride || measuredViewBox)
      ? (() => {
          const vb = viewBoxOverride ?? measuredViewBox!;
          const parts = vb.split(' ').map(Number);
          return {
            x: parts[0],
            y: parts[1],
            width: parts[2],
            height: parts[3],
            fill: 'transparent' as const,
            'data-svg-background': true as const,
          };
        })()
      : autoBackgroundRect;

    // Get format for bond rendering
    const format = useFormatOverride();

    // Build structure for navigable tree (memoized)
    const structure = useMemo(() => {
      if (!showNavigableTree) return null;
      return buildStructure(workspace, featureResult);
    }, [workspace, featureResult, showNavigableTree]);

    // Create default tree label map if not provided (use labels from structure)
    const effectiveTreeLabelMap = useMemo<TreeLabelMap>(() => {
      if (treeLabelMap) return treeLabelMap;
      if (!structure) return new Map();

      // Build a simple tree label map from structure labels
      const map: TreeLabelMap = new Map();

      for (const molecule of structure.tree.molecules) {
        map.set(molecule.id, { name: molecule.label });

        for (const feature of molecule.features) {
          map.set(feature.id, { name: feature.label });
          for (const atom of feature.atoms) {
            map.set(atom.atomId, { name: atom.label });
            for (const lp of atom.lonePairNodes) {
              map.set(lp.lonePairId, { name: lp.label });
            }
          }
          // Bonds (collected from atoms' bondNodes, deduplicated)
          const seenFeatureBondIds = new Set<string>();
          for (const atom of feature.atoms) {
            for (const bond of atom.bondNodes) {
              if (!seenFeatureBondIds.has(bond.bondId)) {
                seenFeatureBondIds.add(bond.bondId);
                map.set(bond.bondId, { name: bond.label });
              }
            }
          }
        }

        for (const atom of molecule.unclaimedAtoms) {
          map.set(atom.atomId, { name: atom.label });
          for (const lp of atom.lonePairNodes) {
            map.set(lp.lonePairId, { name: lp.label });
          }
        }

        // Bonds in unclaimed atoms (collected from bondNodes, deduplicated)
        const seenUnclaimedBondIds = new Set<string>();
        for (const atom of molecule.unclaimedAtoms) {
          for (const bond of atom.bondNodes) {
            if (!seenUnclaimedBondIds.has(bond.bondId)) {
              seenUnclaimedBondIds.add(bond.bondId);
              map.set(bond.bondId, { name: bond.label });
            }
          }
        }
      }

      return map;
    }, [treeLabelMap, structure]);

    // TODO: Layer 3: Motion - Integrate Framer Motion (check for MotionContext)
    // TODO: Layer 4: Interaction - Add zoom/pan, selection, drag, arrow drawing

    // Check if children are provided (indicates accessibility tree is handled externally)
    const hasChildren = React.Children.count(children) > 0;

    return (
      <svg
        ref={mergedRef}
        width={svgWidth}
        height={svgHeight}
        viewBox={viewBox}
        className={className}
        // ARIA: Use role="none" to remove SVG from accessibility tree when tree is provided
        role={(showNavigableTree || hasChildren) ? 'none' : undefined}
        style={{
          border: '1px solid #ddd',
          borderRadius: '4px',
          ...style,
        }}
      >
        {/* SVG definitions for special bonds (dash, wedge, squiggle) and arrow markers */}
        <Defs />

      {/* Background rect for zoom/pan - hidden from screen readers */}
      <rect {...backgroundRect} aria-hidden="true" />
        {/* Navigable ARIA tree for screen reader accessibility */}
        {showNavigableTree && structure && (
          <NavigableTree
            structure={structure}
            treeLabelMap={effectiveTreeLabelMap}
            onSelect={onTreeSelect}
            onFocusChange={onTreeFocusChange}
            debugVisible={debugNavigableTree}
            defaultExpandAll={defaultExpandAll}
          />
        )}

        {/* Render order: bonds → atoms → electrons → charges → arrows */}
        {/* Render bonds from recipes using static atom positions */}
        {/* Hide bonds if either endpoint atom is hidden */}
        {bondRecipes.flatMap((recipe) => {
          const atom1 = atoms.find((a) => a.id === recipe.atom1Id);
          const atom2 = atoms.find((a) => a.id === recipe.atom2Id);

          if (!atom1 || !atom2) {
            console.warn(`[SVGCanvas] Missing atoms for bond ${recipe.bondId}`);
            return [];
          }

          // Hide bond if either endpoint is hidden
          if (atom1.visibility === 'hidden' || atom2.visibility === 'hidden') {
            return [];
          }

          return renderBondFromRecipe(recipe, atom1.x, atom1.y, atom2.x, atom2.y, format);
        })}

        {/* Atoms with position wrappers (graphics rendered at origin) */}
        {/* Apply visibility styling - hidden atoms still render (for data attributes) but are not visible */}
        {atoms.map((atom) => (
          <g
            key={atom.id}
            transform={`translate(${atom.x}, ${atom.y})`}
            style={{ display: atom.visibility === 'hidden' ? 'none' : undefined }}
          >
            {atom.graphics}
          </g>
        ))}

        {electrons}
        {charges}
        {arrows}

        {/* Children (e.g., HTMLStructureTree for accessibility export) - renders at the end */}
        {children}
      </svg>
    );
  }
);
